import { last, replace, split } from 'lodash';
import React from 'react';
import { OTHER, TP } from '../../common/analytics/sourceTypes';
import config from '../../common/config/index';
import ExtraBreadcrumb from '../../components/extraPage/ExtraBreadcrumb';
import { translateSearchType } from '../../components/searchResultPage/advanceSearchUtil';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import SeoHeader from '../../components/seoHeader/SeoHeader';
import Page from '../../layouts/main';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import seoResource from '../../locales/th/seo.json';
import { formatResource } from '../../common/helpers/localization/contentResourceHelper';
import { getSrpTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { DynamicComponentForArticleFeedModule } from '../../components/pages/homePage/DynamicComponents';
import {
  trackAuthorAndTagArticle,
  trackAuthorAndTagArticleFeedShowMore,
  trackAuthorAndTagArticleFeedTab,
} from '../../common/analytics/analyticsTracker';
import { useSelector } from 'react-redux';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import Row from '../../components/rows/Row';
import { WholeState } from '../../store/common/type';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import { SearchParams } from '../searchResultPage/SearchResultPage';

function ExtraPage({
  params,
  extraTitle,
  canonicalLink,
  typeEn,
  seoTitle,
  seoMetaDescription,
}: ExtraPageProps): React.ReactElement {
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const analyticSource = typeEn === 'tag' ? TP.PAGE : OTHER.PAGE;

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticSource}
    >
      <SeoHeader
        title={seoTitle}
        description={seoMetaDescription}
        canonicalLink={canonicalLink}
      />
      <ExtraBreadcrumb extraTitle={extraTitle} />
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        {extraTitle && <ModuleTitle title={extraTitle} />}
        <DynamicComponentForArticleFeedModule
          excludePostIds={[]}
          tabDefinition={getSrpTabDefinition(params)}
          onClickPostCard={(post) =>
            trackAuthorAndTagArticle(userInfo, post, analyticSource)
          }
          onClickArticleFeedShowMore={() => {
            trackAuthorAndTagArticleFeedShowMore(userInfo, analyticSource);
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackAuthorAndTagArticleFeedTab(
              userInfo,
              analyticSource,
              selectedTab,
            );
          }}
        />
      </Row>
      <SeoFooter />
    </Page>
  );
}

ExtraPage.getInitialProps = async ({
  store,
  query,
  asPath,
}: CustomNextPageContext) => {
  const { type, slug } = query;
  const canonicalLink = config.baseUrl + split(asPath, '?')[0].substring(1);
  const lastSlug = last(split(slug as string, '/'));
  const extraTitle = replace(lastSlug, /-/g, ' ');
  const typeEn = translateSearchType(type);
  const params = {
    termsTaxonomy: typeEn,
    termsSlug: lastSlug,
  };

  store.dispatch(
    updateCurrentSourcePage(typeEn === 'tag' ? TP.PAGE : OTHER.PAGE),
  );

  let seoTitle;
  let seoMetaDescription;
  if (type && type.toString().toLowerCase() === 'tag') {
    seoTitle = formatResource(seoResource.tagPage.title, { tag: extraTitle });
    seoMetaDescription = formatResource(seoResource.tagPage.metaDescription, {
      tag: extraTitle,
    });
  }

  if (type && type.toString().toLowerCase() === 'author') {
    seoTitle = formatResource(seoResource.authorPage.title, {
      author: extraTitle,
    });
    seoMetaDescription = formatResource(
      seoResource.authorPage.metaDescription,
      {
        author: extraTitle,
      },
    );
  }

  return {
    params,
    extraTitle,
    canonicalLink,
    typeEn,
    seoTitle,
    seoMetaDescription,
  };
};

interface ExtraPageProps {
  params: SearchParams;
  extraTitle: string;
  canonicalLink: string;
  typeEn: string | string[];
  seoTitle: string;
  seoMetaDescription: string;
}

export default ExtraPage;
